"use client";

import { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Autocomplete,
  TextField,
  Fab,
  Drawer,
  IconButton,
  Button,
  Divider,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { usePathname } from "next/navigation";

import {
  useStatesQuery,
  useCitiesQuery,
  useCategoriesQuery,
} from "@/hooks/useTrendsQueries";
import {
  ALL_CITY,
  CategoryItem,
  CityItem,
  DateRangeKey,
  StateItem,
  useFiltersStore,
} from "@/store/useFilterStore";
import { useFiltersNavigationStore } from "@/store/useFiltersNavigationStore";
import gaService from "@/api/services/ga.service";
import useDevice from "@/hooks/useDevice";

const DATE_RANGE_OPTIONS = [
  { key: "LAST_MONTH" as DateRangeKey, label: "Last Month" },
  { key: "LAST_QUARTER" as DateRangeKey, label: "Last 3 Months" },
  { key: "LAST_HALF_YEAR" as DateRangeKey, label: "Last 6 Months" },
] as const;

type Option = { id: string | number; label: string };

// Special options for Geographical Trends
const ALL_STATES_OPTION: Option = { id: "ALL_STATES", label: "All States" };
const ALL_CITIES_OPTION: Option = { id: "ALL_CITIES", label: "All Cities" };

const autocompleteSx = {
  minWidth: "calc(100% / 4 - 12px)",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    border: "1px solid #ffffff20",
    px: 1,
    py: 0.5,
    "& fieldset": { border: "none" },
  },
  "& .MuiInputLabel-root": {
    color: "#999",
    "&.Mui-focused": { color: "#999" },
    "&.MuiFormLabel-filled": { color: "#999" },
  },
  "& .MuiInputBase-input::placeholder": { color: "#999", opacity: 1 },
};

export default function FiltersBar() {
  const isMobile = useDevice();
  const isMdUp = !isMobile;
  const pathname = usePathname();
  const setSyncing = useFiltersNavigationStore((s) => s.setSyncing);

  const [isAtTop, setIsAtTop] = useState(true);

  // current route is geographical-trends?
  const isGeoTrends = pathname?.includes("geo-trends") ?? false;

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const scrollTop = (e as CustomEvent).detail?.scrollTop || 0;
      setIsAtTop(scrollTop <= 20);
    };

    window.addEventListener(
      "sidebar-content-scroll",
      handleScroll as EventListener
    );

    return () => {
      window.removeEventListener(
        "sidebar-content-scroll",
        handleScroll as EventListener
      );
    };
  }, []);

  const { data: states = [] } = useStatesQuery();
  const { data: categories = [] } = useCategoriesQuery();

  const {
    selectedState,
    selectedCity,
    selectedCategory,
    selectedDateRange,
    setState,
    setCity,
    setCategory,
    setDateRange,
  } = useFiltersStore();

  // Desktop cities use global selectedState as before
  const { data: cities = [] } = useCitiesQuery();

  // ---------- Desktop (md+) : inline + immediate ----------
  if (isMdUp) {
    // for geo-trends, use special options & values
    const stateOptions = isGeoTrends ? [ALL_STATES_OPTION] : (states as Option[]);
    const cityOptions = isGeoTrends ? [ALL_CITIES_OPTION] : (cities as Option[]);
    const stateValue: Option | null = isGeoTrends
      ? ALL_STATES_OPTION
      : ((selectedState as Option) ?? null);
    const cityValue: Option | null = isGeoTrends
      ? ALL_CITIES_OPTION
      : ((selectedCity as Option) ?? null);

    return (
      <Box
        sx={{
          mb: 3,
          position: "sticky",
          top: -25,
          zIndex: 100,
          pt: 2,
          backdropFilter: isAtTop ? "none" : "blur(50px)",
        }}
      >
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Autocomplete
            size="small"
            sx={autocompleteSx}
            options={stateOptions}
            value={stateValue}
            getOptionLabel={(o: Option) => o?.label ?? ""}
            onChange={(_, v) => {
              if (isGeoTrends) return;
              setSyncing(true);
              gaService.stateChanged((v as StateItem)?.label || "");
              setState((v as StateItem) ?? null);
              setCity(ALL_CITY as any);
            }}
            renderInput={(p) => <TextField {...p} label="State" />}
            disableClearable
            disabled={isGeoTrends}
          />
          <Autocomplete
            size="small"
            sx={autocompleteSx}
            options={cityOptions}
            value={cityValue}
            getOptionLabel={(o: Option) => o?.label ?? ""}
            onChange={(_, v) => {
              if (isGeoTrends) return;
              setSyncing(true);
              setCity((v as CityItem) || null);
              gaService.cityChanged((v as CityItem)?.label || "");
            }}
            renderInput={(p) => <TextField {...p} label="City" />}
            disableClearable
            disabled={isGeoTrends}
          />
          <Autocomplete
            size="small"
            sx={autocompleteSx}
            options={categories as Option[]}
            value={(selectedCategory as any) ?? null}
            getOptionLabel={(o: Option) => o?.label ?? ""}
            onChange={(_, v) => {
              setSyncing(true);
              setCategory((v as CategoryItem) ?? null);
              gaService.categoryChanged((v as CategoryItem)?.label || "");
            }}
            renderInput={(p) => <TextField {...p} label="Category" />}
            disableClearable
          />
          <Autocomplete
            size="small"
            sx={autocompleteSx}
            options={DATE_RANGE_OPTIONS as any}
            value={
              (DATE_RANGE_OPTIONS.find(
                (opt) => opt.key === selectedDateRange
              ) as any) ?? null
            }
            getOptionLabel={(o: { label: string }) => o?.label ?? ""}
            onChange={(_, v: any) => {
              setSyncing(true);
              setDateRange(v.key as DateRangeKey);
              gaService.dateRangeChanged(v.label || "");
            }}
            renderInput={(p) => <TextField {...p} label="Date Range" />}
            disableClearable
          />
        </Stack>
      </Box>
    );
  }

  // ---------- Mobile (xs/sm) : FAB + bottom drawer + "See results" ----------
  return (
    <MobileFilters
      isGeoTrends={isGeoTrends}
      states={states as Option[]}
      categories={categories as Option[]}
      selectedState={selectedState as any}
      selectedCity={selectedCity as any}
      selectedCategory={selectedCategory as any}
      selectedDateRange={selectedDateRange}
      setState={(v) => setState(v as StateItem | null)}
      setCity={(v) => setCity(v as CityItem | null)}
      setCategory={(v) => setCategory(v as CategoryItem | null)}
      setDateRange={setDateRange}
    />
  );
}

function MobileFilters({
  isGeoTrends,
  states,
  categories,
  selectedState,
  selectedCity,
  selectedCategory,
  selectedDateRange,
  setState,
  setCity,
  setCategory,
  setDateRange,
}: {
  isGeoTrends: boolean;
  states: Option[];
  categories: Option[];
  selectedState: Option | null;
  selectedCity: Option | null;
  selectedCategory: Option | null;
  selectedDateRange: DateRangeKey | null;
  setState: (v: Option | null) => void;
  setCity: (v: Option | null) => void;
  setCategory: (v: Option | null) => void;
  setDateRange: (v: DateRangeKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const setSyncing = useFiltersNavigationStore((s) => s.setSyncing);

  // Local pending state (only committed on "See results")
  const [pState, setPState] = useState<Option | null>(
    isGeoTrends ? ALL_STATES_OPTION : selectedState ?? null
  );
  const [pCity, setPCity] = useState<Option | null>(
    isGeoTrends ? ALL_CITIES_OPTION : selectedCity ?? (ALL_CITY as any)
  );
  const [pCat, setPCat] = useState<Option | null>(selectedCategory ?? null);
  const [pRange, setPRange] = useState<{ key: DateRangeKey; label: string } | null>(
    (DATE_RANGE_OPTIONS.find((o) => o.key === selectedDateRange) as any) ??
      (DATE_RANGE_OPTIONS[0] as any)
  );

  const stateOptions = isGeoTrends ? [ALL_STATES_OPTION] : states;

  // 🔑 Fetch cities based on *pending* state, without touching global store
  const { data: citiesForPendingState = [] } = useCitiesQuery(
    isGeoTrends ? null : ((pState?.id as string) ?? null)
  );

  const cityOptions = isGeoTrends
    ? [ALL_CITIES_OPTION]
    : (citiesForPendingState as Option[]);

  // When opening, sync pending with current store values
  useEffect(() => {
    if (open) {
      setPState(isGeoTrends ? ALL_STATES_OPTION : selectedState ?? null);
      setPCity(
        isGeoTrends ? ALL_CITIES_OPTION : selectedCity ?? (ALL_CITY as any)
      );
      setPCat(selectedCategory ?? null);
      setPRange(
        (DATE_RANGE_OPTIONS.find((o) => o.key === selectedDateRange) as any) ??
          (DATE_RANGE_OPTIONS[0] as any)
      );
    }
  }, [
    open,
    isGeoTrends,
    selectedState,
    selectedCity,
    selectedCategory,
    selectedDateRange,
  ]);

  // If pending state changes, reset pending city to ALL (only when not geo-trends)
  useEffect(() => {
    if (!isGeoTrends) {
      setPCity(ALL_CITY as any);
    }
  }, [pState?.id, isGeoTrends]);

  const apply = () => {
    setSyncing(true);
    // Commit pending values to global store
    setState(isGeoTrends ? null : pState ?? null);
    setCity(isGeoTrends ? (ALL_CITY as any) : pCity ?? (ALL_CITY as any));
    setCategory(pCat ?? null);
    setDateRange((pRange?.key as DateRangeKey) ?? "LAST_MONTH");
    setOpen(false);
  };

  const reset = () => {
    setPState(isGeoTrends ? ALL_STATES_OPTION : null);
    setPCity(isGeoTrends ? ALL_CITIES_OPTION : (ALL_CITY as any));
    setPCat(null);
    setPRange(DATE_RANGE_OPTIONS[0] as any);
  };

  return (
    <>
      {/* FAB */}
      <Fab
        color="primary"
        onClick={() => setOpen(true)}
        sx={{
          position: "fixed",
          right: 16,
          bottom: 16,
          zIndex: 1200,
          bgcolor: "#6540F7",
          "&:hover": { bgcolor: "#5b4ac2" },
        }}
        aria-label="Filters"
      >
        <FilterListIcon />
      </Fab>

      {/* Bottom Drawer */}
      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            background: "#121212",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 2,
            pb: "max(16px, env(safe-area-inset-bottom))",
          },
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography sx={{ color: "#EAEAEA", fontWeight: 600, fontSize: 16 }}>
            Filters
          </Typography>
          <IconButton
            onClick={() => setOpen(false)}
            aria-label="Close"
            sx={{ color: "#EAEAEA" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "#2A2A2A", mb: 2 }} />

        <Stack spacing={2}>
          <Autocomplete
            size="small"
            sx={autocompleteSx}
            options={stateOptions}
            value={pState ?? null}
            getOptionLabel={(o: Option) => o?.label ?? ""}
            onChange={(_, v) => {
              if (isGeoTrends) return;
              setPState(v ?? null);
            }}
            renderInput={(p) => <TextField {...p} label="State" />}
            disabled={isGeoTrends}
          />
          <Autocomplete
            size="small"
            sx={autocompleteSx}
            options={cityOptions}
            value={pCity ?? (ALL_CITY as any)}
            getOptionLabel={(o: Option) => o?.label ?? ""}
            onChange={(_, v) => {
              if (isGeoTrends) return;
              setPCity(v ?? (ALL_CITY as any));
            }}
            renderInput={(p) => <TextField {...p} label="City" />}
            disabled={isGeoTrends}
          />
          <Autocomplete
            size="small"
            sx={autocompleteSx}
            options={categories}
            value={pCat ?? null}
            getOptionLabel={(o: Option) => o?.label ?? ""}
            onChange={(_, v) => setPCat(v ?? null)}
            renderInput={(p) => <TextField {...p} label="Category" />}
          />
          <Autocomplete
            size="small"
            sx={autocompleteSx}
            options={DATE_RANGE_OPTIONS as any}
            value={pRange as any}
            getOptionLabel={(o: { label: string }) => o?.label ?? ""}
            onChange={(_, v) => setPRange(v as any)}
            renderInput={(p) => <TextField {...p} label="Date Range" />}
          />
        </Stack>

        {/* Actions */}
        <Stack
          direction={{ xs: "column-reverse", md: "row" }}
          spacing={1.5}
          sx={{ mt: 2 }}
        >
          <Button
            onClick={reset}
            variant="outlined"
            fullWidth
            sx={{
              textTransform: "none",
              borderColor: "#2A2A2A",
              color: "#EAEAEA",
              "&:hover": { borderColor: "#3a3a3a", background: "#1B1B1B" },
              borderRadius: 2,
            }}
          >
            Reset
          </Button>
          <Button
            onClick={apply}
            variant="contained"
            fullWidth
            sx={{
              textTransform: "none",
              borderRadius: 2,
              bgcolor: "#6540F7",
              "&:hover": { bgcolor: "#5b4ac2" },
            }}
          >
            See results
          </Button>
        </Stack>
      </Drawer>
    </>
  );
}
